import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData } from "../../utility/Utility";
import Swal from "sweetalert2";
import { CSVLink } from "react-csv";
import { Modal, Button, Tab, Tabs } from "react-bootstrap";
import EmailModal from "../../Common/Emailmodale/Emailmodale";
import CustomLoader from "../../Components/CustomLoader/CustomLoader";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";

function InquiriesManagement() {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalInquiries, setTotalInquiries] = useState(0);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("personal");
  const [showEmailModal, setShowEmailModal] = useState(false);
  const rowsPerPage = 10;

  // Fetch inquiries from API
  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await postData("api/insurance-enquiry/list", {
        limit: rowsPerPage,
        page: currentPage + 1, // API uses 1-based index
        search: searchTerm,
        startDate: startDate,
        endDate: endDate,
      });

      if (response.success) {
        setInquiries(response?.data || []);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotalInquiries(response.pagination?.totalItems || 0);
      } else {
        toast.error(response.message || "Failed to fetch inquiries");
      }
    } catch (error) {
      toast.error("Error fetching inquiries");
      console.error("Fetch inquiries error:", error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, startDate, endDate]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  // Handle page change
  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchInquiries();
  };

  // Reset all filters
  const handleReset = () => {
    setSearchTerm("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(0);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // View inquiry details
  const handleView = (inquiry) => {
    setSelectedInquiry(inquiry);
    setShowModal(true);
    setActiveTab("personal"); // Reset to first tab when opening modal
  };

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedInquiry(null);
  };

  // Download document
  const handleDownload = (url, fileName) => {
    if (!url) {
      toast.warning("No document available to download");
      return;
    }

    // Create a temporary anchor element
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.download = fileName || "document";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Prepare data for CSV export
  const prepareCSVData = () => {
    return inquiries.map((inquiry) => ({
      Name: inquiry.fullName,
      "Father Name": inquiry.fatherName,
      "Mother Name": inquiry.motherName,
      Email: inquiry.emailId,
      Mobile: inquiry.mobileNumber,
      "Alternate Mobile": inquiry.alternateMobileNumber,
      Gender: inquiry.gender || "N/A",
      "Marital Status": inquiry.maritalStatus || "N/A",
      Occupation: inquiry.occupation || "N/A",
      Designation: inquiry.designation || "N/A",
      "Work Experience": inquiry.workExperience || "N/A",
      "Insurance Type": inquiry.insuranceType || "N/A",
      "Nominee Name": inquiry.nomineeName || "N/A",
      "Health Status": inquiry.healthStatus || "N/A",
      "Pre-existing Conditions": inquiry.preExistingConditions || "N/A",
      Height: inquiry.height || "N/A",
      Weight: inquiry.weight || "N/A",
      "Agent Name": inquiry.agentName || "N/A",
      "Inquiry Date": formatDate(inquiry.createdAt),
      "PAN Card": inquiry.documents?.panCard,
      "Aadhaar Card": inquiry.documents?.aadhaarCard,
    }));
  };

  // CSV headers
  const csvHeaders = [
    { label: "Name", key: "Name" },
    { label: "Father Name", key: "Father Name" },
    { label: "Mother Name", key: "Mother Name" },
    { label: "Email", key: "Email" },
    { label: "Mobile", key: "Mobile" },
    { label: "Alternate Mobile", key: "Alternate Mobile" },
    { label: "Gender", key: "Gender" },
    { label: "Marital Status", key: "Marital Status" },
    { label: "Occupation", key: "Occupation" },
    { label: "Designation", key: "designation" },
    { label: "Work Experience", key: "Work Experience" },
    { label: "Insurance Type", key: "Insurance Type" },
    { label: "Nominee Name", key: "Nominee Name" },
    { label: "Health Status", key: "Health Status" },
    { label: "Pre-existing Conditions", key: "Pre-existing Conditions" },
    { label: "Height", key: "Height" },
    { label: "Weight", key: "Weight" },
    { label: "Agent Name", key: "Agent Name" },
    { label: "Inquiry Date", key: "Inquiry Date" },
    { label: "PAN Card", key: "PAN Card" },
    { label: "Aadhaar Card", key: "Aadhaar Card" },
  ];

  // Document types with labels
  const documentTypes = [
    { key: "aadhaarCard", label: "Aadhaar Card" },
    { key: "panCard", label: "PAN Card" },
    { key: "idProof", label: "Voter ID/Passport" },
    { key: "addressProof", label: "Address Proof" },
    { key: "dobProof", label: "Date of Birth Proof" },
    { key: "passportPhoto", label: "Passport Photo" },

    { key: "salarySlipsFile", label: "Salary Slips" },
    { key: "bankStatement", label: "Bank Statement" },
    { key: "itr", label: "Income Tax Return" },
    { key: "fullPhoto", label: "Full Photo" },

    { key: "medicalHistoryForm", label: "Medical History Form" },
    { key: "nomineeIdProof", label: "Nominee ID Proof" },
    { key: "healthCheckupReport", label: "Health Checkup Report" },
    { key: "existingHealthPolicy", label: "Existing Health Policy" },

    { key: "incomeProof", label: "Income Proof" },
    { key: "employmentProof", label: "Employment Proof" },
    { key: "termNomineeIdProof", label: "Term Nominee IdProof" },
  ];

  const handleStatusUpdate = async (enquiryId, status) => {
    let rejectedReason = undefined;
    if (status === "rejected") {
      const { value: reason, isConfirmed } = await Swal.fire({
        title: "Reject Inquiry",
        input: "textarea",
        inputLabel: "Please provide a reason for rejection",
        inputPlaceholder: "Type the reason here...",
        inputValue: "",
        inputAttributes: {
          autocapitalize: "off",
          required: true,
        },
        showCancelButton: true,
        confirmButtonText: "Reject",
        cancelButtonText: "Cancel",
        inputValidator: (value) => {
          if (!value) return "A reason is required!";
        },
      });
      if (isConfirmed && reason) {
        rejectedReason = reason;
      } else {
        toast.info("Rejection cancelled");
        return;
      }
    } else {
      const { isConfirmed } = await Swal.fire({
        title: `Update status to "${
          status.charAt(0).toUpperCase() + status.slice(1)
        }"?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "Cancel",
      });
      if (!isConfirmed) {
        toast.info("Status update cancelled");
        return;
      }
    }
    try {
      const payload = { enquiryId, status };
      if (rejectedReason) payload.rejectedReason = rejectedReason;
      const response = await postData(
        "api/insurance-enquiry/updateAdminEnquiry",
        payload
      );
      if (response?.success) {
        toast.success(response.message || "Enquiry updated successfully");
        fetchInquiries();
      } else {
        throw new Error(response?.message || "Failed to update enquiry");
      }
    } catch (error) {
      toast.error(error.message || "Failed to update enquiry");
      console.error("Error:", error);
    }
  };

  const handleWhatsApp = (inquiry) => {
    const encodedData = encodeURIComponent(btoa(JSON.stringify(inquiry)));
    const formUrl = `http://localhost:5010/update-inquiries-management?data=${encodedData}`;
    const message = `Hello, please fill this form: ${formUrl}`;

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleEmailClick = (inquiry) => {
    setSelectedInquiry(inquiry);
    setShowEmailModal(true);
  };
  return (
    <div className="content-page">
      <CustomLoader loading={loading} />
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="content">
        <div className="container-fluid">
          {/* Header Section */}
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <h4 className="fs-18 fw-semibold m-0">
                Insurance Inquiries Management
              </h4>
              <p className="text-muted mb-0">
                Total Inquiries: {totalInquiries}
              </p>
            </div>
            <div className="mt-2 mt-sm-0">
              {inquiries?.length > 0 && (
                <CSVLink
                  data={prepareCSVData()}
                  headers={csvHeaders}
                  filename={`insurance-inquiries-${new Date()
                    .toISOString()
                    .slice(0, 10)}.csv`}
                  className="btn btn-success"
                >
                  <i className="mdi mdi-file-excel me-1"></i> Export to CSV
                </CSVLink>
              )}
            </div>
          </div>

          {/* Search and Filter Section */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3 align-items-end ">
                      <div className="col-md-12 col-lg-3">
                        <label htmlFor="Search" className="form-label">
                          Search
                        </label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control ps-4"
                            placeholder="Search by name, email or mobile..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <span className="input-group-text">
                            <i className="mdi mdi-magnify"></i>
                          </span>
                        </div>
                      </div>

                      <div className="col-md-12 col-lg-2">
                        <label htmlFor="startDate" className="form-label">
                          From Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          id="startDate"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                        />
                      </div>

                      <div className="col-md-12 col-lg-2">
                        <label htmlFor="endDate" className="form-label">
                          To Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          id="endDate"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          min={startDate}
                        />
                      </div>

                      <div className="col-md-12 col-lg-3 d-flex gap-2 align-items-end">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          Search
                        </button>

                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleReset}
                          disabled={loading}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          {/* Inquiries Table */}
          <div className="row mt-2">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-5">
                      <CustomLoader />
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover table-centered mb-0">
                        <thead>
                          <tr>
                            <th>Image</th>
                            <th>Inquiry Id</th>
                            <th>Name</th>
                            <th>Contact</th>
                            <th>Occupation</th>
                            <th>Insurance Type</th>
                            <th>Status</th>
                            <th>Inquiry Date</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inquiries?.length > 0 ? (
                            inquiries.map((inquiry) => (
                              <tr key={inquiry._id}>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div className="flex-shrink-0 me-2">
                                      <div className="avatar-xs">
                                        <div className="avatar-title rounded-circle bg-light text-primary">
                                          {inquiry.fullName?.charAt(0) || "U"}
                                        </div>
                                      </div>
                                    </div>
                                    {/* <div className="flex-grow-1">
                                                                            <h5 className="fs-14 mb-1">{inquiry.fullName}</h5>
                                                                            <p className="text-muted mb-0">{inquiry.gender || 'N/A'}</p>
                                                                        </div> */}
                                  </div>
                                </td>
                                <td>{inquiry.enquiryId}</td>
                                <td>
                                  <h5 className="fs-14 mb-1">
                                    {inquiry.fullName}
                                  </h5>
                                  <p className="text-muted mb-0">
                                    {inquiry.gender || "N/A"}
                                  </p>
                                </td>
                                <td>
                                  <p className="mb-1">{inquiry.emailId}</p>
                                  <p className="text-muted mb-0">
                                    {inquiry.mobileNumber}
                                  </p>
                                </td>
                                <td>
                                  {inquiry.occupation || "N/A"}
                                  {inquiry.designation &&
                                    ` (${inquiry.designation})`}
                                </td>
                                <td>{inquiry.insuranceType || "N/A"}</td>
                                <td>
                                  <span
                                    className={`badge bg-${
                                      inquiry.status === "approved"
                                        ? "success"
                                        : inquiry.status === "pending"
                                        ? "warning"
                                        : inquiry.status === "rejected"
                                        ? "danger"
                                        : "secondary"
                                    }`}
                                  >
                                    {inquiry.status?.charAt(0).toUpperCase() +
                                      inquiry.status?.slice(1) || "N/A"}
                                  </span>
                                </td>
                                <td>{formatDate(inquiry.createdAt)}</td>
                                <td>
                                  <div className="d-flex gap-1">
                                    <button
                                      className="btn btn-sm btn-primary"
                                      title="Edit"
                                      onClick={() =>
                                        navigate(
                                          "/update-inquiries-management",
                                          { state: { inquiry } }
                                        )
                                      }
                                    >
                                      <i className="mdi mdi-pencil"></i>
                                    </button>
                                    <div className="dropdown">
                                      <button
                                        className="btn btn-sm btn-primary dropdown-toggle"
                                        type="button"
                                        data-bs-toggle="dropdown"
                                        title="Update Status"
                                      >
                                        <i className="mdi mdi-dots-vertical"></i>
                                      </button>
                                      <ul className="dropdown-menu">
                                        <li>
                                          <button
                                            className="dropdown-item"
                                            onClick={() =>
                                              handleStatusUpdate(
                                                inquiry._id,
                                                "approved"
                                              )
                                            }
                                          >
                                            Approved
                                          </button>
                                        </li>
                                        <li>
                                          <button
                                            className="dropdown-item"
                                            onClick={() =>
                                              handleStatusUpdate(
                                                inquiry._id,
                                                "pending"
                                              )
                                            }
                                          >
                                            Pending
                                          </button>
                                        </li>
                                        <li>
                                          <button
                                            className="dropdown-item"
                                            onClick={() =>
                                              handleStatusUpdate(
                                                inquiry._id,
                                                "rejected"
                                              )
                                            }
                                          >
                                            Rejected
                                          </button>
                                        </li>
                                      </ul>
                                    </div>
                                    <button
                                      className="btn btn-sm btn-primary"
                                      onClick={() => handleView(inquiry)}
                                      title="View"
                                    >
                                      <i className="mdi mdi-eye"></i>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-success"
                                      onClick={() => handleWhatsApp(inquiry)}
                                      title="Send on WhatsApp"
                                    >
                                      <i className="mdi mdi-whatsapp"></i>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-info"
                                      onClick={() => handleEmailClick(inquiry)}
                                      title="Send Email"
                                    >
                                      <i className="mdi mdi-email"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="8" className="text-center py-4">
                                No inquiries found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <ReactPaginate
                          previousLabel={
                            <i className="mdi mdi-chevron-left"></i>
                          }
                          nextLabel={<i className="mdi mdi-chevron-right"></i>}
                          breakLabel="..."
                          breakClassName="page-item"
                          breakLinkClassName="page-link"
                          pageCount={totalPages}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={5}
                          onPageChange={handlePageChange}
                          containerClassName="pagination pagination-rounded justify-content-end mt-3"
                          pageClassName="page-item"
                          pageLinkClassName="page-link"
                          previousClassName="page-item"
                          previousLinkClassName="page-link"
                          nextClassName="page-item"
                          nextLinkClassName="page-link"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <EmailModal
        showEmailModal={showEmailModal}
        setShowEmailModal={setShowEmailModal}
        selectedInquiry={selectedInquiry}
      />

      {/* Inquiry Details Modal */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="xl"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Inquiry Details - {selectedInquiry?.fullName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedInquiry && (
            <>
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-3"
              >
                <Tab eventKey="personal" title="Personal Info">
                  <div className="row">
                    <div className="col-md-6">
                      <h5 className="mb-3">Basic Information</h5>
                      <div className="table-responsive">
                        <table className="table table-sm table-borderless">
                          <tbody>
                            <tr>
                              <th width="40%">Full Name:</th>
                              <td>{selectedInquiry.fullName}</td>
                            </tr>
                            <tr>
                              <th>Father's Name:</th>
                              <td>{selectedInquiry.fatherName || "N/A"}</td>
                            </tr>
                            <tr>
                              <th>Mother's Name:</th>
                              <td>{selectedInquiry.motherName || "N/A"}</td>
                            </tr>

                            <tr>
                              <th>Gender:</th>
                              <td>{selectedInquiry.gender || "N/A"}</td>
                            </tr>
                            <tr>
                              <th>Marital Status:</th>
                              <td>{selectedInquiry.maritalStatus || "N/A"}</td>
                            </tr>
                            <tr>
                              <th>Spouse Name:</th>
                              <td>{selectedInquiry.spouseName || "N/A"}</td>
                            </tr>
                            <tr>
                              <th>Has Children:</th>
                              <td>{selectedInquiry.hasChildren || "N/A"}</td>
                            </tr>
                            <tr>
                              <th>Children:</th>
                              <td>
                                {selectedInquiry.children &&
                                selectedInquiry.children.length > 0 ? (
                                  <ul
                                    style={{ paddingLeft: "1rem", margin: 0 }}
                                  >
                                    {selectedInquiry.children.map(
                                      (child, index) => (
                                        <li key={index}>
                                          Name: {child.name || "N/A"} - Age:{" "}
                                          {child.age || "N/A"} - Gender:{" "}
                                          {child.gender || "N/A"}
                                        </li>
                                      )
                                    )}
                                  </ul>
                                ) : (
                                  "N/A"
                                )}
                              </td>
                            </tr>
                            <tr>
                              <th>Smoker:</th>
                              <td>{selectedInquiry.smoker ? "Yes" : "No"}</td>
                            </tr>
                            <tr>
                              <th>Nationality:</th>
                              <td>{selectedInquiry.nationality || "N/A"}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <h5 className="mb-3">Contact Information</h5>
                      <div className="table-responsive">
                        <table className="table table-sm table-borderless">
                          <tbody>
                            <tr>
                              <th width="40%">Email:</th>
                              <td>{selectedInquiry.emailId}</td>
                            </tr>
                            <tr>
                              <th>Mobile:</th>
                              <td>{selectedInquiry.mobileNumber}</td>
                            </tr>
                            <tr>
                              <th>Alternate Mobile:</th>
                              <td>
                                {selectedInquiry.alternateMobileNumber || "N/A"}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <h5 className="mb-3 mt-4">Health Information</h5>
                      <div className="table-responsive">
                        <table className="table table-sm table-borderless">
                          <tbody>
                            <tr>
                              <th width="40%">Health Status:</th>
                              <td>{selectedInquiry.healthStatus || "N/A"}</td>
                            </tr>
                            <tr>
                              <th>Pre-existing Conditions:</th>
                              <td>
                                {selectedInquiry.preExistingConditions || "N/A"}
                              </td>
                            </tr>
                            <tr>
                              <th>Height:</th>
                              <td>{selectedInquiry.height || "N/A"}</td>
                            </tr>
                            <tr>
                              <th>Weight:</th>
                              <td>{selectedInquiry.weight || "N/A"}</td>
                            </tr>
                            <tr>
                              <th>Surgery History:</th>
                              <td>
                                {selectedInquiry.surgeryHistory ? "Yes" : "No"}
                              </td>
                            </tr>
                            <tr>
                              <th>Current Medication:</th>
                              <td>{selectedInquiry.isOnMedication || "N/A"}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </Tab>

                <Tab eventKey="professional" title="Professional Info">
                  <div className="row">
                    <div className="col-md-6">
                      <h5 className="mb-3">Employment Details</h5>
                      <div className="table-responsive">
                        <table className="table table-sm table-borderless">
                          <tbody>
                            <tr>
                              <th width="40%">Occupation:</th>
                              <td>{selectedInquiry.occupation || "N/A"}</td>
                            </tr>
                            <tr>
                              <th>Organization:</th>
                              <td>
                                {selectedInquiry.organizationName || "N/A"}
                              </td>
                            </tr>
                            <tr>
                              <th>Designation:</th>
                              <td>{selectedInquiry.designation || "N/A"}</td>
                            </tr>
                            <tr>
                              <th>Work Experience:</th>
                              <td>{selectedInquiry.workExperience || "N/A"}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <h5 className="mb-3">Insurance Details</h5>
                      <div className="table-responsive">
                        <table className="table table-sm table-borderless">
                          <tbody>
                            <tr>
                              <th width="40%">Insurance Type:</th>
                              <td>{selectedInquiry.insuranceType || "N/A"}</td>
                            </tr>
                            <tr>
                              <th>Existing Insurance:</th>
                              <td>
                                {selectedInquiry.existingInsurance
                                  ? "Yes"
                                  : "No"}
                              </td>
                            </tr>
                            <tr>
                              <th>Education:</th>
                              <td>
                                {selectedInquiry.educationalQualification ||
                                  "N/A"}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <h5 className="mb-3 mt-4">Nominee Information</h5>
                      <div className="table-responsive">
                        <table className="table table-sm table-borderless">
                          <tbody>
                            <tr>
                              <th width="40%">Nominee Name:</th>
                              <td>{selectedInquiry.nomineeName || "N/A"}</td>
                            </tr>
                            <tr>
                              <th>Nominee DOB:</th>
                              <td>
                                {selectedInquiry.nomineeDOB
                                  ? formatDate(selectedInquiry.nomineeDOB)
                                  : "N/A"}
                              </td>
                            </tr>
                            <tr>
                              <th>Nominee Relation:</th>
                              <td>
                                {selectedInquiry.nomineeRelationship || "N/A"}
                              </td>
                            </tr>
                            <tr>
                              <th>Alternate Nominee:</th>
                              <td>
                                {selectedInquiry.alternateNomineeName || "N/A"}
                              </td>
                            </tr>
                            <tr>
                              <th>Alternate Nominee Relation:</th>
                              <td>
                                {selectedInquiry.alternateNomineeRelationship ||
                                  "N/A"}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </Tab>

                <Tab eventKey="documents" title="Documents">
                  <div className="row">
                    {documentTypes.map((doc) => (
                      <div className="col-md-4 mb-3" key={doc.key}>
                        <div className="card h-100">
                          <div className="card-body">
                            <h6 className="card-title">{doc.label}</h6>
                            {selectedInquiry.documents?.[doc.key] ? (
                              <>
                                {typeof selectedInquiry.documents[doc.key] ===
                                "string" ? (
                                  <>
                                    <div className="mb-2">
                                      <img
                                        src={selectedInquiry.documents[doc.key]}
                                        alt={doc.label}
                                        className="img-thumbnail"
                                        style={{
                                          maxHeight: "150px",
                                          width: "auto",
                                        }}
                                      />
                                    </div>
                                    <button
                                      className="btn btn-sm btn-primary"
                                      onClick={() =>
                                        handleDownload(
                                          selectedInquiry.documents[doc.key],
                                          doc.label
                                        )
                                      }
                                    >
                                      <i className="mdi mdi-download me-1"></i>{" "}
                                      Download
                                    </button>
                                  </>
                                ) : (
                                  <p className="text-success">
                                    Document submitted
                                  </p>
                                )}
                              </>
                            ) : (
                              <p className="text-muted">
                                No document submitted
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Tab>

                <Tab eventKey="system" title="System Info">
                  <div className="row">
                    <div className="col-md-6">
                      <h5 className="mb-3">System Information</h5>
                      <div className="table-responsive">
                        <table className="table table-sm table-borderless">
                          <tbody>
                            <tr>
                              <th width="40%">Enquiry Id:</th>
                              <td>{selectedInquiry.enquiryId}</td>
                            </tr>
                            <tr>
                              <th width="40%">Agent Name:</th>
                              <td>{selectedInquiry.agentName || "N/A"}</td>
                            </tr>

                            <tr>
                              <th width="40%">Agent Number:</th>
                              <td>{selectedInquiry.agentNumber || "N/A"}</td>
                            </tr>
                            <tr>
                              <th>Created At:</th>
                              <td>{formatDate(selectedInquiry.createdAt)}</td>
                            </tr>
                            <tr>
                              <th>Updated At:</th>
                              <td>{formatDate(selectedInquiry.updatedAt)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </Tab>
              </Tabs>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default InquiriesManagement;
